import { useEffect, useMemo, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Clipboard, FileText, KeyRound, Loader2, Search } from 'lucide-react';

const iconMap = {
  'Operator Guide': Clipboard,
  'Admin Guide': KeyRound,
  'Integration Guide': FileText,
  'Platform Reference': BookOpen,
};

export default function OperatorManualPage() {
  const [docs, setDocs] = useState([]);
  const [activeDoc, setActiveDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docLoading, setDocLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    const fetchDocs = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get('/operator-docs');
        setDocs(data);
        if (data[0]) await openDoc(data[0].id);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    };
    fetchDocs();
  }, []);

  const openDoc = async (docId) => {
    setDocLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/operator-docs/${docId}`);
      setActiveDoc(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setDocLoading(false);
    }
  };

  const filteredDocs = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return docs;
    return docs.filter(doc => `${doc.title} ${doc.description} ${doc.category}`.toLowerCase().includes(needle));
  }, [docs, query]);

  const copyPath = async () => {
    if (!activeDoc?.path) return;
    setCopyStatus('');
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard is unavailable');
      await navigator.clipboard.writeText(activeDoc.path);
      setCopyStatus('Path copied.');
    } catch {
      setCopyStatus('Copy unavailable. Select the file path text manually.');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="operator-manual-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Badge variant="secondary" className="mb-3 bg-zinc-900 text-white" data-testid="operator-manual-badge">Admin Manual</Badge>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 font-heading" data-testid="operator-manual-title">Operator Manual</h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-500" data-testid="operator-manual-description">
            FieldCRM playbooks for lead lifecycle operations, DBA setup, API keys, quote acceptance, and landing-page integrations.
          </p>
        </div>
        <div className="relative w-full lg:w-80">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search docs..."
            className="h-10 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-sm outline-none transition-colors focus:border-zinc-500"
            data-testid="operator-manual-search-input"
          />
        </div>
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="operator-manual-error">{error}</div>}

      <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
        <div className="space-y-3" data-testid="operator-manual-doc-list">
          {loading ? (
            <Card className="border border-zinc-200"><CardContent className="flex items-center gap-2 py-6 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading docs...</CardContent></Card>
          ) : filteredDocs.length === 0 ? (
            <Card className="border border-zinc-200"><CardContent className="py-6 text-sm text-zinc-500">No matching docs.</CardContent></Card>
          ) : filteredDocs.map(doc => {
            const Icon = iconMap[doc.category] || FileText;
            const isActive = activeDoc?.id === doc.id;
            return (
              <button
                key={doc.id}
                type="button"
                onClick={() => openDoc(doc.id)}
                className={`w-full rounded-md border p-4 text-left transition-colors ${isActive ? 'border-zinc-950 bg-zinc-950 text-white' : 'border-zinc-200 bg-white hover:border-zinc-400'}`}
                data-testid={`operator-doc-card-${doc.id}`}
              >
                <div className="flex items-start gap-3">
                  <Icon className={`mt-0.5 h-4 w-4 ${isActive ? 'text-white' : 'text-zinc-500'}`} />
                  <div className="min-w-0">
                    <p className="font-semibold" data-testid={`operator-doc-title-${doc.id}`}>{doc.title}</p>
                    <p className={`mt-1 text-xs ${isActive ? 'text-zinc-300' : 'text-zinc-500'}`}>{doc.description}</p>
                    <Badge variant="secondary" className={`mt-2 text-[10px] ${isActive ? 'bg-white/10 text-white' : ''}`}>{doc.category}</Badge>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <Card className="min-h-[620px] border border-zinc-200" data-testid="operator-manual-doc-viewer">
          <CardHeader className="border-b border-zinc-200">
            {activeDoc ? (
              <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                <div>
                  <CardTitle className="text-xl font-bold font-heading" data-testid="operator-manual-active-title">{activeDoc.title}</CardTitle>
                  <p className="mt-1 text-xs text-zinc-500" data-testid="operator-manual-active-path">{activeDoc.path}</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={copyPath} data-testid="operator-manual-copy-path-button">
                  <BookOpen className="mr-1 h-3.5 w-3.5" /> Copy file path
                </Button>
                {copyStatus && <p className="text-xs text-zinc-500" data-testid="operator-manual-copy-status">{copyStatus}</p>}
              </div>
            ) : <CardTitle className="text-xl font-bold font-heading">Select a guide</CardTitle>}
          </CardHeader>
          <CardContent className="p-0">
            {docLoading ? (
              <div className="flex items-center gap-2 p-6 text-sm text-zinc-500" data-testid="operator-manual-doc-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading guide...</div>
            ) : activeDoc ? (
              <article className="max-h-[68vh] overflow-auto whitespace-pre-wrap break-words p-5 font-mono text-xs leading-6 text-zinc-800" data-testid="operator-manual-doc-content">
                {activeDoc.content}
              </article>
            ) : (
              <div className="p-6 text-sm text-zinc-500">Choose a document from the list.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}